import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import {
  User,
  createNewAccessRecord,
  updateExistingAccessRecord,
  deleteExistingAccessRecord,
} from "@/api/api";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ErrorMessage } from "@/components/ui/error-message";
import PersonaCards from "@/components/PersonaCards";
import { DeleteUserDialog } from "./DeleteUserDialog";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { adminQueries } from "@/api/api";
import { BadgeInput } from "../ui/badgeInput";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Check } from "lucide-react";
import { type Persona, usePersonasQuery } from "@/api/personas";
import { Role } from "../AuthorizationProvider";

const ROLE_OPTIONS = [
  { label: "Creator", value: Role.Creator },
  { label: "Tenant Admin", value: Role.TenantAdmin },
  { label: "Super Admin", value: Role.SuperAdmin },
];

const USAGE_TIER_OPTIONS = [
  { label: "Standard", value: "Standard" },
  { label: "Premium", value: "Premium" },
  { label: "Unlimited", value: "Unlimited" },
];

interface UserFormProps {
  isNewUser: boolean;
  initialUser?: Partial<User>;
  tenants: { id: string; name: string }[];
  personas: Persona[];
  onSuccess: () => void;
  handleback?: () => void;
}

/**
 * In this form:
 * - `email` is the only user identifier in the UI.
 * - We map `email -> username` when saving to backend.
 * - `usage_tier`, `personas` (IDs), `default_persona` (ID), etc. remain the same.
 */
export function UserForm({
  isNewUser,
  initialUser,
  tenants,
  personas = [],
  onSuccess,
  handleback,
}: UserFormProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: availablePersonas = [] } = usePersonasQuery();

  // 1) Local states
  //    The UI treats "username" as "email".
  //    We'll map them for the backend calls.
  const [userChanges, setUserChanges] = useState<Partial<User>>(() => {
    // If we have an initial user, treat `initialUser.username` as `email` in the UI
    // Also note we now store persona_id in `personas` & `default_persona`
    return {
      email: initialUser?.username ?? "",
      qstarlabs_tenant: initialUser?.qstarlabs_tenant ?? "",
      // Make sure these are arrays/IDs (not names)
      personas: initialUser?.personas ?? [],
      default_persona: initialUser?.default_persona ?? "",
      usage_tier: initialUser?.usage_tier ?? "Standard",
      role: initialUser?.role ?? Role.Creator,
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // We'll show persona cards for any relevant subset
  const [filteredPersonas, setFilteredPersonas] = useState(personas);

  // If brand-new user => reset to blank
  useEffect(() => {
    if (isNewUser) {
      setUserChanges({
        email: "",
        qstarlabs_tenant: "",
        personas: [],
        default_persona: "",
        usage_tier: "Standard",
      });
    }
  }, [isNewUser]);

  // 2) The "final" object the UI shows
  const selectedUser = { ...userChanges };

  // 3) Filter personas automatically by role or tenant
  useEffect(() => {
    if (!personas || personas.length === 0) {
      setFilteredPersonas([]);
      return;
    }

    if (selectedUser.qstarlabs_tenant) {
      // e.g. filter by tenant
      setFilteredPersonas(
        personas.filter(
          (p) => p.qstarlabs_tenant === selectedUser.qstarlabs_tenant,
        ),
      );
    } else {
      setFilteredPersonas(personas);
    }
  }, [personas, selectedUser.qstarlabs_tenant]);

  // 4) Generic field change
  const handleFieldChange = (field: string, value: string) => {
    // If user typed or changed "personas" as a CSV, parse into array of IDs
    if (field === "personas") {
      const personaIds = value ? value.split(",").map((id) => id.trim()) : [];

      // If the default persona is no longer in the array, reset it
      if (
        userChanges.default_persona &&
        !personaIds.includes(userChanges.default_persona)
      ) {
        const newDefault = personaIds[0] ?? ""; // pick first or blank
        setUserChanges((prev) => ({
          ...prev,
          default_persona: newDefault,
          personas: personaIds,
        }));

        if (newDefault) {
          toast({
            title: "Default Persona Changed",
            description: `Your default persona was removed from the list; automatically set to: ${newDefault}`,
          });
        } else {
          toast({
            title: "No Default Persona",
            description:
              "Your default persona was removed, and no new default was set.",
          });
        }
      } else {
        setUserChanges((prev) => ({ ...prev, personas: personaIds }));
      }
    } else {
      setUserChanges((prev) => ({ ...prev, [field]: value }));
    }
  };

  // 5) Validate
  const validateFields = () => {
    const validationErrors: Record<string, string> = {};

    // Email is the only user ID the UI shows
    if (!selectedUser.email) {
      validationErrors.email = "Email is required";
    }
    if (!selectedUser.qstarlabs_tenant) {
      validationErrors.qstarlabs_tenant = "Tenant is required";
    }
    if (!selectedUser.role) {
      validationErrors.role = "Role is required";
    }
    if (!selectedUser.usage_tier) {
      validationErrors.usage_tier = "Usage tier is required";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Additional check for invalid persona IDs
  const validateBeforeSave = () => {
    // We check that each persona_id in userChanges.personas
    // actually exists in availablePersonas
    const invalidPersonaIds = (selectedUser.personas || []).filter(
      (pId) => !availablePersonas.some((p) => p.persona_id === pId),
    );

    if (invalidPersonaIds.length > 0) {
      toast({
        variant: "destructive",
        title: "Invalid Agents",
        description: (
          <>
            <p>
              The following agent IDs are invalid or removed. Please remove them
              from the persona list to proceed:
            </p>
            <p>{invalidPersonaIds.join(", ")}</p>
          </>
        ),
      });
      return false;
    }

    return true;
  };

  // 6) Save
  const saveUser = async () => {
    if (!validateFields() || !validateBeforeSave()) return;

    // The backend expects `username` instead of `email`.
    const payload = {
      username: selectedUser.email!, // rename for backend
      role: selectedUser.role!,
      qstarlabs_tenant: selectedUser.qstarlabs_tenant!,
      // Here we pass persona IDs
      personas: selectedUser.personas ?? [],
      // default_persona is also an ID
      default_persona: selectedUser.default_persona ?? "",
      usage_tier: selectedUser.usage_tier ?? "Standard",
    };

    try {
      if (isNewUser) {
        await createNewAccessRecord(payload);
      } else {
        // "username" is the key
        await updateExistingAccessRecord(payload.username, payload);
      }
      toast({ title: "Success", description: "User saved successfully!" });
      queryClient.invalidateQueries(adminQueries.users());
      onSuccess();
    } catch (error: any) {
      console.log(error);
      toast({
        variant: "destructive",
        description:
          "Error saving user: " + (error.response?.data?.detail || ""),
      });
    }
  };

  // 7) Delete
  const handleDeleteUser = async () => {
    try {
      await deleteExistingAccessRecord(selectedUser.email);
      toast({ title: "User Deleted", description: "User has been deleted." });
      queryClient.invalidateQueries(adminQueries.users());
      onSuccess();
    } catch {
      toast({
        variant: "destructive",
        description: "Failed to delete user. Please try again.",
      });
    }
  };

  /**
   * Toggle the persona_id in userChanges.personas
   */
  const handlePersonaSelect = (persona: {
    persona_id: string;
    name: string;
  }) => {
    const currentPersonaIds = selectedUser.personas || [];
    const isSelected = currentPersonaIds.includes(persona.persona_id);
    let newPersonas: string[];

    if (isSelected) {
      // remove it
      newPersonas = currentPersonaIds.filter(
        (pid) => pid !== persona.persona_id,
      );
    } else {
      // add it
      newPersonas = [...currentPersonaIds, persona.persona_id];
    }

    // Then we store them as a comma-separated string to pass to handleFieldChange
    handleFieldChange("personas", newPersonas.join(","));
  };

  // Render
  return (
    <div className="space-y-6">
      {/* Basic fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email => main user ID */}
        <div>
          <Label>Email</Label>
          <Input
            value={selectedUser.email || ""}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            placeholder="Enter user email"
            disabled={!isNewUser} // If editing user, can't change
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </div>

        {/* Tenant */}
        <div>
          <Label>Tenant</Label>
          <Select
            value={selectedUser.qstarlabs_tenant || ""}
            onValueChange={(val) => handleFieldChange("qstarlabs_tenant", val)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select tenant" />
            </SelectTrigger>
            <SelectContent>
              {tenants.length > 0 ? (
                tenants.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </SelectItem>
                ))
              ) : (
                // @ts-expect-error missing value
                <SelectItem disabled>No tenants available</SelectItem>
              )}
            </SelectContent>
          </Select>
          {errors.qstarlabs_tenant && (
            <ErrorMessage>{errors.qstarlabs_tenant}</ErrorMessage>
          )}
        </div>

        {/* Role */}
        <div>
          <Label>Role</Label>
          <Select
            value={selectedUser.role || ""}
            onValueChange={(val) => handleFieldChange("role", val)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.role && <ErrorMessage>{errors.role}</ErrorMessage>}
        </div>

        {/* Usage Tier */}
        <div>
          <Label>Usage Tier</Label>
          <Select
            value={selectedUser.usage_tier || "Standard"}
            onValueChange={(val) => handleFieldChange("usage_tier", val)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select usage tier" />
            </SelectTrigger>
            <SelectContent>
              {USAGE_TIER_OPTIONS.map((tier) => (
                <SelectItem key={tier.value} value={tier.value}>
                  {tier.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.usage_tier && (
            <ErrorMessage>{errors.usage_tier}</ErrorMessage>
          )}
        </div>

        {/* Personas (IDs), comma-separated */}
        <div>
          <Label className="flex flex-col gap-y-2">
            Agents (Comma-separated) [Stores persona_id]
            <Popover>
              <PopoverTrigger>
                <BadgeInput
                  disabled={false}
                  // We display the persona_id array for now.
                  // If you prefer the persona's name, you'd have to map ID->Name here.
                  value={selectedUser.personas || []}
                  onChange={(newIds) =>
                    handleFieldChange("personas", newIds.join(","))
                  }
                  placeholder="Add an agent"
                  readOnly
                />
              </PopoverTrigger>
              <PopoverContent className="p-0">
                <Command>
                  <CommandInput placeholder="Search agents..." />
                  <CommandList>
                    <CommandEmpty>No agents found.</CommandEmpty>
                    <CommandGroup>
                      {availablePersonas
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((persona) => {
                          const isChecked = selectedUser.personas?.includes(
                            persona.persona_id,
                          );
                          return (
                            <CommandItem
                              key={persona.persona_id}
                              value={persona.persona_id}
                              onSelect={() => handlePersonaSelect(persona)}
                              keywords={[persona.name]}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  isChecked ? "opacity-100" : "opacity-0",
                                )}
                              />
                              {/* Display persona.name, but store persona_id */}
                              {persona.name}
                            </CommandItem>
                          );
                        })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Label>
        </div>

        {/* Default Persona (ID) */}
        <div>
          <Label>Default Persona</Label>
          <Select
            value={selectedUser.default_persona || ""}
            onValueChange={(val) => handleFieldChange("default_persona", val)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select default persona" />
            </SelectTrigger>
            <SelectContent>
              {(selectedUser.personas || []).map((pid) => {
                const found = availablePersonas.find(
                  (p) => p.persona_id === pid,
                );
                // If the persona is found, show its name; else fallback to pid
                return (
                  <SelectItem key={pid} value={pid}>
                    {found ? found.name : pid}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Save & Delete */}
      <div className="flex justify-end gap-4">
        {/* Only show Delete if editing existing user */}
        {!isNewUser && (
          <DeleteUserDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            onDelete={handleDeleteUser}
          >
            <Button
              variant="ghost"
              className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete User
            </Button>
          </DeleteUserDialog>
        )}
        {isNewUser && (
          <Button onClick={handleback} variant="outline">
            Cancel
          </Button>
        )}
        <Button onClick={saveUser} variant="default">
          Save User
        </Button>
      </div>

      {/* Persona Cards (filtered) */}
      <div className="mt-6">
        <Label>Available Personas</Label>
        {filteredPersonas.length > 0 ? (
          <PersonaCards
            personas={filteredPersonas}
            onSelectPersona={(personaId) =>
              console.log(`Selected Persona: ${personaId}`)
            }
          />
        ) : (
          <p className="text-center text-muted-foreground">
            No personas available to display.
          </p>
        )}
      </div>
    </div>
  );
}
